import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-redux-i18n';
import StockAvatar from 'components/StockAvatar';
import { layoutTreemap } from 'views/Report/components/SymbolSummary/treemapLayout';
import 'assets/css/report.css';

const VIEW_W = 900;
const VIEW_H = 520;
const PAD = 1.5;

function getLivePrice(item, stockMap) {
    const live = stockMap?.[item.symbol];
    const current = Number(live?.c);
    if (current > 0) {
        return current;
    }
    const previousClose = Number(live?.pc);
    if (previousClose > 0) {
        return previousClose;
    }
    const fallback = Number(item.price);
    return fallback > 0 ? fallback : 0;
}

function calcPl(quantity, openPrice, currentPrice, market) {
    const qty = Number(quantity);
    const open = Math.abs(Number(openPrice));
    const current = Number(currentPrice);
    if (!qty || !current || Number.isNaN(open)) {
        return null;
    }
    if (market === 'SELL') {
        return qty * (open - current);
    }
    return qty * (current - open);
}

function calcPlPercent(openPrice, currentPrice, market) {
    const open = Math.abs(Number(openPrice));
    const current = Number(currentPrice);
    if (!current || Number.isNaN(open) || !open) {
        return null;
    }
    if (market === 'SELL') {
        return ((open - current) / open) * 100;
    }
    return ((current - open) / open) * 100;
}

function getPositionSize(order) {
    if (order.counter > 1 && order.sub_order?.length) {
        return order.sub_order.reduce(
            (sum, sub) => sum + Math.abs(Number(sub.open_trade) || 0),
            0,
        );
    }
    return Math.abs(Number(order.open_trade) || 0);
}

function getOrderPl(order, currentPrice) {
    if (order.counter > 1 && order.sub_order?.length) {
        let total = 0;
        let hasValue = false;
        order.sub_order.forEach((sub) => {
            const pl = calcPl(sub.quantity, sub.open_price, currentPrice, sub.market);
            if (pl != null) {
                total += pl;
                hasValue = true;
            }
        });
        return hasValue ? total : null;
    }
    return calcPl(order.quantity, order.open_price, currentPrice, order.market);
}

function getOrderPlPercent(order, currentPrice) {
    if (order.counter > 1 && order.sub_order?.length) {
        const pl = getOrderPl(order, currentPrice);
        const invested = getPositionSize(order);
        if (pl == null || !invested) {
            return null;
        }
        return (pl / invested) * 100;
    }
    return calcPlPercent(order.open_price, currentPrice, order.market);
}

/**
 * Continuous gradient by P/L %:
 *   0% → black
 *  +% → black → green
 *  -% → black → red
 * Full color around ±FULL_PCT (absolute).
 */
const FULL_PCT = 40;

function smoothstep(t) {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
}

function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
}

function plPctHeatColor(plPct) {
    const pct = Number(plPct) || 0;
    const t = smoothstep(Math.abs(pct) / FULL_PCT);
    // near-black base so 0% reads as black
    const black = { r: 8, g: 8, b: 8 };
    if (pct >= 0) {
        // → #16a34a (green-600)
        return `rgb(${lerp(black.r, 22, t)}, ${lerp(black.g, 163, t)}, ${lerp(black.b, 74, t)})`;
    }
    // → #dc2626 (red-600)
    return `rgb(${lerp(black.r, 220, t)}, ${lerp(black.g, 38, t)}, ${lerp(black.b, 38, t)})`;
}

/** SVG legend strip: red ← black → green */
function GradientLegend() {
    const stops = [];
    const steps = 24;
    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        // left half: -FULL_PCT → 0, right half: 0 → +FULL_PCT
        const pct = -FULL_PCT + t * (FULL_PCT * 2);
        stops.push(
            <stop
                key={i}
                offset={`${(t * 100).toFixed(2)}%`}
                stopColor={plPctHeatColor(pct)}
            />,
        );
    }
    return (
        <div className="portfolio-heatmap-legend" aria-hidden="true">
            <span className="portfolio-heatmap-legend-label">-{FULL_PCT}%</span>
            <svg className="portfolio-heatmap-legend-bar" viewBox="0 0 240 10" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="portfolioHeatGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        {stops}
                    </linearGradient>
                </defs>
                <rect x="0" y="0" width="240" height="10" rx="3" fill="url(#portfolioHeatGrad)" />
            </svg>
            <span className="portfolio-heatmap-legend-label">+{FULL_PCT}%</span>
        </div>
    );
}

const PositionHeatmapModal = ({ open, list, stock, onClose }) => {
    const cells = useMemo(() => {
        const rows = (Array.isArray(list) ? list : [])
            .map((order) => {
                const currentPrice = getLivePrice(order, stock);
                const size = getPositionSize(order);
                const pl = getOrderPl(order, currentPrice);
                const plPct = getOrderPlPercent(order, currentPrice);
                return {
                    id: order.symbol,
                    symbol: order.symbol,
                    value: size,
                    pl: pl == null ? 0 : pl,
                    plPct: plPct == null ? 0 : plPct,
                    item: {
                        symbol: order.symbol,
                        icon_url: order.icon_url,
                        logo_url: order.logo_url,
                    },
                };
            })
            .filter((item) => item.value > 0);

        return layoutTreemap(rows, VIEW_W, VIEW_H).map((cell) => ({
            ...cell,
            color: plPctHeatColor(cell.plPct),
        }));
    }, [list, stock]);

    if (!open) {
        return null;
    }

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="report-heatmap-overlay"
            onClick={handleBackdropClick}
            role="presentation"
        >
            <div
                className="report-heatmap-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="portfolio-heatmap-title"
            >
                <div className="report-heatmap-header">
                    <div className="report-heatmap-header-text">
                        <h4 id="portfolio-heatmap-title">
                            <Translate value="portfolio_heatmap" />
                        </h4>
                        <p className="report-heatmap-subtitle">
                            <Translate value="portfolio_heatmap_hint" />
                        </p>
                        <GradientLegend />
                    </div>
                    <div className="report-heatmap-header-actions">
                        <button
                            type="button"
                            className="btn btn-text btn-sm report-heatmap-close"
                            onClick={onClose}
                        >
                            <Translate value="close" />
                        </button>
                    </div>
                </div>

                <div className="report-heatmap-body">
                    {cells.length === 0 ? (
                        <p className="report-heatmap-empty">
                            <Translate value="portfolio_heatmap_empty" />
                        </p>
                    ) : (
                        <svg
                            className="report-heatmap-svg"
                            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                            role="img"
                            aria-label="Position heat map"
                        >
                            {cells.map((cell) => {
                                const showPct = cell.width > 78 && cell.height > 56;
                                const showDetail = cell.width > 70 && cell.height > 42;
                                const showSymbol = cell.width > 36 && cell.height > 24;
                                const x = cell.x + PAD;
                                const y = cell.y + PAD;
                                const w = Math.max(0, cell.width - PAD * 2);
                                const h = Math.max(0, cell.height - PAD * 2);
                                const sizeText = `$${cell.value.toFixed(0)}`;
                                const pctText = `${cell.plPct >= 0 ? '+' : ''}${cell.plPct.toFixed(1)}%`;
                                const plText = `${cell.pl >= 0 ? '+' : ''}${cell.pl.toFixed(0)}`;

                                // Avatar on large tiles; size grows with the cell
                                const minSide = Math.min(w, h);
                                const showAvatar = minSide >= 70;
                                const avatarSize = showAvatar
                                    ? Math.max(24, Math.min(72, Math.floor(minSide * 0.36)))
                                    : 0;
                                const gap = showAvatar ? 6 : 0;
                                const lineH = 14;
                                const textLines = (showSymbol ? 1 : 0)
                                    + (showDetail ? 1 : 0)
                                    + (showPct ? 1 : 0);
                                const textBlockH = textLines * lineH;
                                const contentH = avatarSize + (showAvatar && textLines ? gap : 0) + textBlockH;
                                const contentTop = y + Math.max(4, (h - contentH) / 2);
                                const avatarX = x + (w - avatarSize) / 2;
                                const textStartY = contentTop + avatarSize + (showAvatar ? gap : 0) + 11;
                                let textLine = 0;
                                const nextTextY = () => {
                                    const ty = textStartY + textLine * lineH;
                                    textLine += 1;
                                    return ty;
                                };

                                return (
                                    <g key={cell.symbol}>
                                        <title>
                                            {`${cell.symbol}: ${sizeText} · P/L $${plText} (${pctText})`}
                                        </title>
                                        <rect
                                            x={x}
                                            y={y}
                                            width={w}
                                            height={h}
                                            rx={4}
                                            ry={4}
                                            fill={cell.color}
                                        />
                                        {showAvatar && (
                                            <foreignObject
                                                x={avatarX}
                                                y={contentTop}
                                                width={avatarSize}
                                                height={avatarSize}
                                            >
                                                <div
                                                    xmlns="http://www.w3.org/1999/xhtml"
                                                    className="portfolio-heatmap-avatar-wrap"
                                                    style={{ width: avatarSize, height: avatarSize }}
                                                >
                                                    <StockAvatar
                                                        symbol={cell.symbol}
                                                        item={cell.item}
                                                        className="portfolio-heatmap-avatar"
                                                    />
                                                </div>
                                            </foreignObject>
                                        )}
                                        {showSymbol && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-symbol"
                                            >
                                                {cell.symbol}
                                            </text>
                                        )}
                                        {showDetail && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-pl"
                                            >
                                                {sizeText}
                                            </text>
                                        )}
                                        {showPct && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-pct"
                                            >
                                                {pctText}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}                        </svg>
                    )}
                </div>
            </div>
        </div>
    );
};

PositionHeatmapModal.propTypes = {
    open: PropTypes.bool,
    list: PropTypes.array,
    stock: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

PositionHeatmapModal.defaultProps = {
    open: false,
    list: [],
    stock: {},
};

export default PositionHeatmapModal;
